const SECTION_ALIASES = {
    findings: 'findings',
    finding: 'findings',
    impression: 'impression',
    recommendations: 'recommendations',
    recommendation: 'recommendations',
    technique: 'technique',
    comparison: 'comparison',
    history: 'clinicalHistory',
    'clinical history': 'clinicalHistory',
    clinicalhistory: 'clinicalHistory',
};
/**
 * Exact spoken phrases the built-in dictation grammar consumes (including the
 * section alias words, which trigger bare-name jumps). Custom voice bindings
 * must not collide with these; the server mirrors this list in
 * backend/WebApi/src/Controllers/Users/PutUserPreferences.cs
 * (BuiltinDictationPhrases) and viewer-voice-commands.test.ts pins the
 * combined set so the two cannot drift silently.
 */
export const BUILTIN_DICTATION_PHRASES = [
    'new paragraph',
    'new line',
    'scratch that',
    'select that',
    'sign report',
    'sign the report',
    'submit',
    'sign and next',
    'sign and next case',
    'stop',
    'stop dictation',
    'next field',
    'previous field',
    'prior field',
    'compare to prior',
    ...Object.keys(SECTION_ALIASES),
];
/**
 * Command prefixes the grammar owns ('select <phrase>', 'insert <macro>',
 * 'go to <section>'). A custom binding starting with one of these could never
 * fire because the grammar matches first, so validation rejects them. Also
 * mirrored server-side next to BuiltinDictationPhrases.
 */
export const RESERVED_COMMAND_PREFIXES = ['select ', 'insert ', 'go to '];
/**
 * Built-in phrases grouped by purpose. Consumed by the help popover and the
 * tests that pin its coverage. The union of these groups plus the section alias words is
 * exactly BUILTIN_DICTATION_PHRASES (pinned in dictation-commands.test.ts).
 */
export const BUILTIN_COMMAND_GROUPS = {
    dictation: [
        'new paragraph',
        'new line',
        'scratch that',
        'select that',
        'sign report',
        'sign the report',
        'submit',
        'sign and next',
        'sign and next case',
        'stop',
        'stop dictation',
    ],
    navigation: ['next field', 'previous field', 'prior field', 'compare to prior'],
};
function normalizeCommandText(text) {
    return text
        .trim()
        .replace(/^[\s.,!?]+/, '')
        .replace(/[\s.,!?]+$/, '')
        .toLowerCase();
}
/**
 * Normalizes a spoken phrase or a section label for matching: lowercased,
 * punctuation stripped, hyphens and slashes read as spaces, whitespace
 * collapsed. Both sides of every comparison (spoken text, template labels,
 * custom binding phrases) go through this so "Follow-up" matches "follow up".
 */
export function normalizeVoicePhrase(text) {
    return text
        .toLowerCase()
        .replace(/[.,!?:;'"()‘’“”]/g, '')
        .replace(/[-/\u2013\u2014]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
/**
 * Resolves a spoken section reference against the live section list.
 *
 * Matching order: static alias (only when its target key exists in the list,
 * so "history" cannot strand dictation on a section the template does not
 * render) -> exact normalized label -> exact key -> unique startsWith on
 * label -> unique contains on label. The partial tiers are opt-in: bare-name
 * jumps use exact tiers only, so dictated content words never jump on a
 * prefix; "go to X" gets the full ladder. Two or more matches at any tier
 * resolve to null so ambiguous words insert as text instead of jumping wrong.
 */
function resolveSectionKey(raw, sections, allowPartial) {
    const spoken = normalizeVoicePhrase(raw);
    if (!spoken)
        return null;
    const alias = SECTION_ALIASES[spoken];
    if (alias && sections.some((s) => s.key === alias))
        return alias;
    const labeled = sections.filter((s) => s.label);
    const exactLabel = labeled.filter((s) => normalizeVoicePhrase(s.label) === spoken);
    if (exactLabel.length === 1)
        return exactLabel[0].key;
    if (exactLabel.length > 1)
        return null;
    const exactKey = sections.find((s) => s.key.toLowerCase() === spoken);
    if (exactKey)
        return exactKey.key;
    if (!allowPartial)
        return null;
    const startsWith = labeled.filter((s) => normalizeVoicePhrase(s.label).startsWith(spoken));
    if (startsWith.length === 1)
        return startsWith[0].key;
    if (startsWith.length > 1)
        return null;
    const contains = labeled.filter((s) => normalizeVoicePhrase(s.label).includes(spoken));
    if (contains.length === 1)
        return contains[0].key;
    return null;
}
/**
 * Parse a final STT segment into a command or null (insert verbatim).
 * Section jumps match against the live section list (keys AND labels), so
 * custom-template sections are reachable by their spoken label.
 */
export function parseDictationCommand(segment, sections) {
    const normalized = normalizeCommandText(segment);
    if (!normalized)
        return null;
    if (normalized === 'new paragraph') {
        return { type: 'insert', text: '\n\n' };
    }
    if (normalized === 'new line') {
        return { type: 'insert', text: '\n' };
    }
    if (normalized === 'scratch that') {
        return { type: 'scratch_that' };
    }
    if (normalized === 'select that') {
        return { type: 'select_last_insertion' };
    }
    const selectMatch = normalized.match(/^select (.+)$/);
    if (selectMatch?.[1]) {
        return { type: 'select_phrase', phrase: selectMatch[1].trim() };
    }
    if (normalized === 'sign report' || normalized === 'sign the report' || normalized === 'submit') {
        return { type: 'sign_report' };
    }
    if (normalized === 'sign and next' || normalized === 'sign and next case') {
        return { type: 'sign_and_next' };
    }
    if (normalized === 'stop' || normalized === 'stop dictation') {
        return { type: 'stop_dictation' };
    }
    if (normalized === 'next field') {
        return { type: 'navigate_placeholder', direction: 'next' };
    }
    if (normalized === 'previous field' || normalized === 'prior field') {
        return { type: 'navigate_placeholder', direction: 'previous' };
    }
    if (normalized === 'compare to prior') {
        // Falls back to the standard comparison key even when the template does
        // not render it (unchanged legacy behavior): the prior-reference panel is
        // still worth opening, and focusSection no-ops on a missing textarea.
        const sectionKey = resolveSectionKey('comparison', sections, false) ?? 'comparison';
        return { type: 'show_prior_reference', sectionKey };
    }
    const insertMatch = normalized.match(/^insert (.+)$/);
    if (insertMatch?.[1]) {
        return { type: 'insert_macro', query: insertMatch[1].trim() };
    }
    // Bare section name spoken as the ENTIRE final segment. Exact tiers only
    // (alias, normalized label, key): a lone content word that merely prefixes a
    // label must insert as text, never jump.
    const bareSection = resolveSectionKey(normalized, sections, false);
    if (bareSection) {
        return { type: 'focus_section', sectionKey: bareSection };
    }
    const goToMatch = normalized.match(/^go to (.+)$/);
    if (goToMatch) {
        const sectionKey = resolveSectionKey(goToMatch[1], sections, true);
        if (sectionKey)
            return { type: 'focus_section', sectionKey };
    }
    return null;
}
